import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Eye,
  Ban,
  Check,
  Plus,
  Pencil,
  Trash2,
  Settings,
  Power,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const formatAmount = (amount, currency = 'KZT') => {
  if (!amount) return '0';
  return `${amount.toLocaleString('ru-RU')} ₸`;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('ru-RU');
};

const RiskBadge = ({ level }) => {
  const config = {
    low: { label: 'Низкий', className: 'bg-emerald-500/20 text-emerald-400' },
    medium: { label: 'Средний', className: 'bg-amber-500/20 text-amber-400' },
    high: { label: 'Высокий', className: 'bg-orange-500/20 text-orange-400' },
    critical: { label: 'Критический', className: 'bg-red-500/20 text-red-400' },
  };
  const c = config[level] || config.medium;
  return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
};

const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'На проверке', className: 'bg-amber-500/20 text-amber-400', icon: Clock },
    approved: { label: 'Одобрен', className: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
    blocked: { label: 'Заблокирован', className: 'bg-red-500/20 text-red-400', icon: XCircle },
    review: { label: 'Требует проверки', className: 'bg-purple-500/20 text-purple-400', icon: Eye },
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;
  return (
    <Badge variant="outline" className={c.className}>
      <Icon className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
};

const RULE_TYPES = [
  { value: 'amount_limit', label: 'Лимит суммы' },
  { value: 'velocity', label: 'Частота транзакций' },
  { value: 'country_block', label: 'Блокировка страны' },
  { value: 'card_block', label: 'Блокировка карты' },
  { value: 'ip_block', label: 'Блокировка IP' },
];

const ACTIONS = [
  { value: 'alert', label: 'Уведомление' },
  { value: 'review', label: 'На проверку' },
  { value: 'block', label: 'Блокировка' },
];

const AMLPage = () => {
  const { hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('alerts');
  
  // Alerts state
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');

  // Rules state
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [ruleDialog, setRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteRuleDialog, setDeleteRuleDialog] = useState(false);
  const [deletingRule, setDeletingRule] = useState(null);
  const [ruleSaving, setRuleSaving] = useState(false);
  
  const [ruleForm, setRuleForm] = useState({
    name: '',
    rule_type: 'amount_limit',
    action: 'alert',
    is_active: true,
    parameters: {
      threshold: '',
      currency: 'KZT',
      time_window_minutes: '',
      max_transactions: '',
      country_codes: '',
      card_masks: '',
      ip_addresses: '',
    },
  });

  const canWrite = hasPermission('aml', 'write');
  const canBlock = hasPermission('aml', 'block');

  // ============= Alerts functions =============
  const fetchStats = async () => {
    try {
      const response = await api.get('/aml/stats');
      setStats(response.data);
    } catch (err) {
      setStats({
        total_alerts: 0,
        pending_count: 0,
        blocked_count: 0,
        approved_count: 0,
        high_risk_count: 0,
        alerts_today: 0,
        blocked_amount_today: 0,
      });
    }
  };

  const fetchAlerts = async () => {
    try {
      setAlertsLoading(true);
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (riskFilter && riskFilter !== 'all') params.append('risk_level', riskFilter);
      
      const response = await api.get(`/aml/alerts?${params}`);
      setAlerts(response.data.alerts || []);
    } catch (err) {
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleApprove = async (alertId) => {
    try {
      await api.post(`/aml/alerts/${alertId}/approve`);
      toast.success('Транзакция одобрена');
      fetchAlerts();
      fetchStats();
      setSelectedAlert(null);
    } catch (err) {
      toast.error('Ошибка одобрения');
    }
  };

  const handleBlock = async (alertId) => {
    try {
      await api.post(`/aml/alerts/${alertId}/block`);
      toast.success('Транзакция заблокирована');
      fetchAlerts();
      fetchStats();
      setSelectedAlert(null);
    } catch (err) {
      toast.error('Ошибка блокировки');
    }
  };

  // ============= Rules functions =============
  const fetchRules = async () => {
    try {
      setRulesLoading(true);
      const response = await api.get('/aml/rules');
      setRules(response.data.rules || []);
    } catch (err) {
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  };

  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      rule_type: 'amount_limit',
      action: 'alert',
      is_active: true,
      parameters: {
        threshold: '',
        currency: 'KZT',
        time_window_minutes: '',
        max_transactions: '',
        country_codes: '',
        card_masks: '',
        ip_addresses: '',
      },
    });
  };

  const openCreateRuleDialog = () => {
    setEditingRule(null);
    resetRuleForm();
    setRuleDialog(true);
  };

  const openEditRuleDialog = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      rule_type: rule.rule_type,
      action: rule.action,
      is_active: rule.is_active,
      parameters: {
        threshold: rule.parameters?.threshold || '',
        currency: rule.parameters?.currency || 'KZT',
        time_window_minutes: rule.parameters?.time_window_minutes || '',
        max_transactions: rule.parameters?.max_transactions || '',
        country_codes: Array.isArray(rule.parameters?.country_codes) 
          ? rule.parameters.country_codes.join(', ') 
          : (rule.parameters?.country_codes || ''),
        card_masks: Array.isArray(rule.parameters?.card_masks)
          ? rule.parameters.card_masks.join(', ')
          : (rule.parameters?.card_masks || ''),
        ip_addresses: Array.isArray(rule.parameters?.ip_addresses)
          ? rule.parameters.ip_addresses.join(', ')
          : (rule.parameters?.ip_addresses || ''),
      },
    });
    setRuleDialog(true);
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    
    if (!ruleForm.name.trim()) {
      toast.error('Введите название правила');
      return;
    }

    try {
      setRuleSaving(true);
      
      // Build parameters based on rule type
      const parameters = {};
      
      if (ruleForm.rule_type === 'amount_limit') {
        parameters.threshold = parseFloat(ruleForm.parameters.threshold) || 0;
        parameters.currency = ruleForm.parameters.currency;
      } else if (ruleForm.rule_type === 'velocity') {
        parameters.time_window_minutes = parseInt(ruleForm.parameters.time_window_minutes) || 60;
        parameters.max_transactions = parseInt(ruleForm.parameters.max_transactions) || 10;
      } else if (ruleForm.rule_type === 'country_block') {
        parameters.country_codes = ruleForm.parameters.country_codes
          .split(',')
          .map(c => c.trim().toUpperCase())
          .filter(c => c);
      } else if (ruleForm.rule_type === 'card_block') {
        parameters.card_masks = ruleForm.parameters.card_masks
          .split(',')
          .map(c => c.trim())
          .filter(c => c);
      } else if (ruleForm.rule_type === 'ip_block') {
        parameters.ip_addresses = ruleForm.parameters.ip_addresses
          .split(',')
          .map(c => c.trim())
          .filter(c => c);
      }

      const payload = {
        name: ruleForm.name,
        rule_type: ruleForm.rule_type,
        action: ruleForm.action,
        is_active: ruleForm.is_active,
        parameters,
      };

      if (editingRule) {
        await api.put(`/aml/rules/${editingRule.id}`, payload);
        toast.success('Правило обновлено');
      } else {
        await api.post('/aml/rules', payload);
        toast.success('Правило создано');
      }
      
      setRuleDialog(false);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setRuleSaving(false);
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await api.post(`/aml/rules/${rule.id}/toggle`);
      toast.success(rule.is_active ? 'Правило отключено' : 'Правило включено');
      fetchRules();
    } catch (err) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const handleDeleteRule = async () => {
    if (!deletingRule) return;
    
    try {
      await api.delete(`/aml/rules/${deletingRule.id}`);
      toast.success('Правило удалено');
      setDeleteRuleDialog(false);
      setDeletingRule(null);
      fetchRules();
    } catch (err) {
      toast.error('Ошибка удаления');
    }
  };

  // ============= Effects =============
  useEffect(() => {
    fetchStats();
    fetchAlerts();
    fetchRules();
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [statusFilter, riskFilter]);

  // ============= Render helpers =============
  const renderRuleTypeFields = () => {
    switch (ruleForm.rule_type) {
      case 'amount_limit':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Порог суммы</Label>
              <Input
                type="number"
                value={ruleForm.parameters.threshold}
                onChange={(e) => setRuleForm({
                  ...ruleForm,
                  parameters: { ...ruleForm.parameters, threshold: e.target.value }
                })}
                placeholder="1000000"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">{t('terminals.currency')}</Label>
              <Select
                value={ruleForm.parameters.currency}
                onValueChange={(v) => setRuleForm({
                  ...ruleForm,
                  parameters: { ...ruleForm.parameters, currency: v }
                })}
              >
                <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="KZT">KZT</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="RUB">RUB</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      
      case 'velocity':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Окно времени (минуты)</Label>
              <Input
                type="number"
                value={ruleForm.parameters.time_window_minutes}
                onChange={(e) => setRuleForm({
                  ...ruleForm,
                  parameters: { ...ruleForm.parameters, time_window_minutes: e.target.value }
                })}
                placeholder="60"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Макс. транзакций</Label>
              <Input
                type="number"
                value={ruleForm.parameters.max_transactions}
                onChange={(e) => setRuleForm({
                  ...ruleForm,
                  parameters: { ...ruleForm.parameters, max_transactions: e.target.value }
                })}
                placeholder="10"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
          </div>
        );
      
      case 'country_block':
        return (
          <div className="space-y-2">
            <Label className="text-slate-300">Коды стран (через запятую)</Label>
            <Input
              value={ruleForm.parameters.country_codes}
              onChange={(e) => setRuleForm({
                ...ruleForm,
                parameters: { ...ruleForm.parameters, country_codes: e.target.value }
              })}
              placeholder="RU, BY, UA"
              className="bg-slate-700/50 border-slate-600 text-white"
            />
            <p className="text-xs text-slate-500">Используйте ISO 3166-1 alpha-2 коды</p>
          </div>
        );
      
      case 'card_block':
        return (
          <div className="space-y-2">
            <Label className="text-slate-300">Маски карт (через запятую)</Label>
            <Input
              value={ruleForm.parameters.card_masks}
              onChange={(e) => setRuleForm({
                ...ruleForm,
                parameters: { ...ruleForm.parameters, card_masks: e.target.value }
              })}
              placeholder="4111****1111, 5500****0001"
              className="bg-slate-700/50 border-slate-600 text-white"
            />
          </div>
        );
      
      case 'ip_block':
        return (
          <div className="space-y-2">
            <Label className="text-slate-300">IP адреса (через запятую)</Label>
            <Input
              value={ruleForm.parameters.ip_addresses}
              onChange={(e) => setRuleForm({
                ...ruleForm,
                parameters: { ...ruleForm.parameters, ip_addresses: e.target.value }
              })}
              placeholder="192.168.1.1, 10.0.0.0/8"
              className="bg-slate-700/50 border-slate-600 text-white"
            />
            <p className="text-xs text-slate-500">Поддерживаются IP и CIDR нотации</p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="aml-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">AML / Антифрод</h1>
          <p className="text-slate-400 mt-1">Мониторинг и правила безопасности</p>
        </div>
        <Button
          onClick={() => { fetchStats(); fetchAlerts(); fetchRules(); }}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <p className="text-xs text-slate-400">Всего алертов</p>
              <p className="text-xl font-bold text-white">{stats.total_alerts}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4">
              <p className="text-xs text-amber-400">На проверке</p>
              <p className="text-xl font-bold text-amber-400">{stats.pending_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4">
              <p className="text-xs text-red-400">Заблокировано</p>
              <p className="text-xl font-bold text-red-400">{stats.blocked_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-4">
              <p className="text-xs text-emerald-400">Одобрено</p>
              <p className="text-xl font-bold text-emerald-400">{stats.approved_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-500/10 border-orange-500/30">
            <CardContent className="p-4">
              <p className="text-xs text-orange-400">Высокий риск</p>
              <p className="text-xl font-bold text-orange-400">{stats.high_risk_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <p className="text-xs text-slate-400">Сегодня</p>
              <p className="text-xl font-bold text-white">{stats.alerts_today}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="alerts" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Алерты ({alerts.length})
          </TabsTrigger>
          <TabsTrigger value="rules" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Правила ({rules.length})
          </TabsTrigger>
        </TabsList>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-slate-700/50 border-slate-600 text-white">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все</SelectItem>
                <SelectItem value="pending">На проверке</SelectItem>
                <SelectItem value="approved">Одобренные</SelectItem>
                <SelectItem value="blocked">Заблокированные</SelectItem>
              </SelectContent>
            </Select>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-40 bg-slate-700/50 border-slate-600 text-white">
                <SelectValue placeholder="Риск" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все</SelectItem>
                <SelectItem value="low">{t('aml.low')}</SelectItem>
                <SelectItem value="medium">{t('aml.medium')}</SelectItem>
                <SelectItem value="high">{t('aml.high')}</SelectItem>
                <SelectItem value="critical">Критический</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Alerts Table */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-0">
              {alertsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
              ) : alerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <Shield className="w-12 h-12 mb-4 opacity-50" />
                  <p>Алертов нет</p>
                  <p className="text-sm mt-2">Система мониторинга активна</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-4 text-sm font-medium text-slate-400">{t('payments.id')}</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-400">{t('commissions.type')}</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-400">Риск</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.status')}</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-400">{t('payments.amount')}</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.date')}</th>
                        <th className="text-right p-4 text-sm font-medium text-slate-400">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alerts.map((alert) => (
                        <tr key={alert.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="p-4 text-white font-medium">#{alert.payment_id}</td>
                          <td className="p-4 text-slate-300">{alert.alert_type}</td>
                          <td className="p-4"><RiskBadge level={alert.risk_level} /></td>
                          <td className="p-4"><StatusBadge status={alert.status} /></td>
                          <td className="p-4 text-white">{formatAmount(alert.amount)}</td>
                          <td className="p-4 text-slate-400 text-sm">{formatDate(alert.created_at)}</td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedAlert(alert)}
                                className="text-slate-400 hover:text-white"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {alert.status === 'pending' && canWrite && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleApprove(alert.id)}
                                    className="text-emerald-400 hover:text-emerald-300"
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  {canBlock && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleBlock(alert.id)}
                                      className="text-red-400 hover:text-red-300"
                                    >
                                      <Ban className="w-4 h-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          {canWrite && (
            <div className="flex justify-end">
              <Button
                onClick={openCreateRuleDialog}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="create-rule-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Новое правило
              </Button>
            </div>
          )}

          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-0">
              {rulesLoading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
              ) : rules.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <Settings className="w-12 h-12 mb-4 opacity-50" />
                  <p>Правил не создано</p>
                  {canWrite && (
                    <Button
                      onClick={openCreateRuleDialog}
                      className="mt-4 bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Создать первое правило
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-4 text-sm font-medium text-slate-400">Название</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-400">{t('commissions.type')}</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-400">{t('audit.action')}</th>
                        <th className="text-center p-4 text-sm font-medium text-slate-400">{t('common.status')}</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-400">Создано</th>
                        <th className="text-right p-4 text-sm font-medium text-slate-400">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rules.map((rule) => (
                        <tr key={rule.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`rule-row-${rule.id}`}>
                          <td className="p-4 text-white font-medium">{rule.name}</td>
                          <td className="p-4 text-slate-300">
                            {RULE_TYPES.find(t => t.value === rule.rule_type)?.label || rule.rule_type}
                          </td>
                          <td className="p-4">
                            <Badge variant="outline" className={
                              rule.action === 'block' ? 'bg-red-500/20 text-red-400' :
                              rule.action === 'review' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-blue-500/20 text-blue-400'
                            }>
                              {ACTIONS.find(a => a.value === rule.action)?.label || rule.action}
                            </Badge>
                          </td>
                          <td className="p-4 text-center">
                            {rule.is_active ? (
                              <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400">
                                <Check className="w-3 h-3 mr-1" />
                                Активно
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-slate-500/20 text-slate-400">
                                <XCircle className="w-3 h-3 mr-1" />
                                Отключено
                              </Badge>
                            )}
                          </td>
                          <td className="p-4 text-slate-400 text-sm">{formatDate(rule.created_at)}</td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canWrite && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleRule(rule)}
                                    className={rule.is_active ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'}
                                    title={rule.is_active ? 'Отключить' : 'Включить'}
                                  >
                                    <Power className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditRuleDialog(rule)}
                                    className="text-slate-400 hover:text-white"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setDeletingRule(rule); setDeleteRuleDialog(true); }}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Alert Detail Dialog */}
      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Алерт #{selectedAlert?.payment_id}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedAlert?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedAlert && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Риск</p>
                  <RiskBadge level={selectedAlert.risk_level} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">{t('common.status')}</p>
                  <StatusBadge status={selectedAlert.status} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">{t('payments.amount')}</p>
                  <p className="text-white font-medium">{formatAmount(selectedAlert.amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{t('payments.card')}</p>
                  <p className="text-white font-mono">{selectedAlert.card_mask || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">IP</p>
                  <p className="text-white font-mono">{selectedAlert.ip_address || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Страна</p>
                  <p className="text-white">{selectedAlert.country_code || 'N/A'}</p>
                </div>
              </div>

              {selectedAlert.reviewed_by && (
                <div className="p-3 bg-slate-700/30 rounded-lg">
                  <p className="text-xs text-slate-500">Проверил</p>
                  <p className="text-white">{selectedAlert.reviewed_by}</p>
                  <p className="text-xs text-slate-400">{formatDate(selectedAlert.reviewed_at)}</p>
                  {selectedAlert.notes && (
                    <p className="text-slate-300 mt-2 text-sm">{selectedAlert.notes}</p>
                  )}
                </div>
              )}

              {selectedAlert.status === 'pending' && canWrite && (
                <DialogFooter className="gap-2">
                  <Button
                    onClick={() => handleApprove(selectedAlert.id)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Одобрить
                  </Button>
                  {canBlock && (
                    <Button
                      onClick={() => handleBlock(selectedAlert.id)}
                      variant="destructive"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Заблокировать
                    </Button>
                  )}
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rule Create/Edit Dialog */}
      <Dialog open={ruleDialog} onOpenChange={setRuleDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-emerald-500" />
              {editingRule ? 'Редактировать правило' : 'Новое правило'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSaveRule} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Название *</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder="Лимит крупных платежей"
                className="bg-slate-700/50 border-slate-600 text-white"
                required
                data-testid="rule-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Тип правила</Label>
                <Select
                  value={ruleForm.rule_type}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, rule_type: v })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="rule-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">{t('audit.action')}</Label>
                <Select
                  value={ruleForm.action}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, action: v })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="rule-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-slate-700 pt-4">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Параметры правила</h4>
              {renderRuleTypeFields()}
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <Label className="text-slate-300">Правило активно</Label>
              <Switch
                checked={ruleForm.is_active}
                onCheckedChange={(checked) => setRuleForm({ ...ruleForm, is_active: checked })}
                data-testid="rule-active-switch"
              />
            </div>

            <DialogFooter className="mt-6 pt-4 border-t border-slate-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRuleDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={ruleSaving}
                data-testid="save-rule-btn"
              >
                {ruleSaving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                {editingRule ? 'Сохранить' : 'Создать'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Rule Confirmation Dialog */}
      <Dialog open={deleteRuleDialog} onOpenChange={setDeleteRuleDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Удалить правило?
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-300 mt-2">
            Вы уверены, что хотите удалить правило <strong>{deletingRule?.name}</strong>?
          </p>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteRuleDialog(false)}
              className="border-slate-600 text-slate-300"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteRule}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-rule-btn"
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AMLPage;
